/* Phase 1: native modal ownership, not Dawn's unrelated header focus trap.
 * URL and other history keys remain untouched. Entries live only in this page
 * instance; stale restored entries never reopen a new page's Shop. */
(() => {
  const KEY = '__rtcgShop';
  const mobileViewport = window.matchMedia('(max-width: 989px)');
  let viewportRaf = 0;
  let viewportBaseline = 0;
  let retargetingShellTap = false;

  // iOS 26/WebKit has an open regression around fixed bottom-anchored UI:
  // what is painted and what receives the tap can diverge as browser chrome or
  // the software keyboard changes the visual viewport. Keep one measured
  // visual-viewport height in CSS and anchor the shell from the top instead of
  // relying on bottom:0. This is harmless on other mobile engines and avoids a
  // browser-specific user-agent fork.
  let viewportCssHeightApplied = false;
  const isEditable = (element = document.activeElement) => Boolean(
    element?.matches?.('input, textarea, select, [contenteditable="true"]')
  );
  const setKeyboardState = (open, height = 0) => {
    const body = document.body;
    if (!body) return;
    if (body.classList.contains('rtcg-mobile-keyboard-open') !== open) {
      body.classList.toggle('rtcg-mobile-keyboard-open', open);
    }
    // Normal browser-chrome movement is handled by CSS 100dvh natively. Only
    // publish a JS viewport height while the software keyboard is genuinely
    // reducing the visible viewport. This avoids a style/layout write on every
    // Safari toolbar animation frame, which made the bottom shell visibly lag.
    if (open) {
      const next = `${Math.max(1, Math.round(height))}px`;
      if (document.documentElement.style.getPropertyValue('--rtcg-mobile-vv-height') !== next) {
        document.documentElement.style.setProperty('--rtcg-mobile-vv-height', next);
      }
      viewportCssHeightApplied = true;
    } else if (viewportCssHeightApplied) {
      document.documentElement.style.removeProperty('--rtcg-mobile-vv-height');
      viewportCssHeightApplied = false;
    }
  };
  const syncVisualViewport = (resetBaseline = false) => {
    viewportRaf = 0;
    if (!mobileViewport.matches) {
      setKeyboardState(false);
      viewportBaseline = 0;
      return;
    }
    const vv = window.visualViewport;
    const height = Math.max(1, Math.round(vv?.height || window.innerHeight || document.documentElement.clientHeight || 1));
    const editable = isEditable();

    if (resetBaseline || !viewportBaseline) viewportBaseline = Math.max(height, Math.round(window.innerHeight || height));
    if (!editable) {
      viewportBaseline = Math.max(viewportBaseline, height, Math.round(window.innerHeight || height));
      setKeyboardState(false);
      return;
    }

    const keyboardOpen = viewportBaseline - height > Math.max(120, viewportBaseline * 0.18);
    setKeyboardState(keyboardOpen, height);
  };
  const scheduleVisualViewport = (resetBaseline = false) => {
    if (viewportRaf) cancelAnimationFrame(viewportRaf);
    viewportRaf = requestAnimationFrame(() => syncVisualViewport(resetBaseline));
  };
  const installVisualViewportTracking = () => {
    if (window.__rtcgMobileViewportTracking) return;
    window.__rtcgMobileViewportTracking = true;
    syncVisualViewport(true);
    // Do not track visualViewport.scroll. Safari emits it continuously while
    // browser chrome animates; the shell now follows 100dvh in CSS without JS.
    window.addEventListener('resize', () => {
      if (isEditable() || document.body?.classList.contains('rtcg-mobile-keyboard-open')) scheduleVisualViewport();
    }, { passive: true });
    window.addEventListener('orientationchange', () => scheduleVisualViewport(true), { passive: true });
    window.addEventListener('pageshow', () => scheduleVisualViewport(true), { passive: true });
    window.visualViewport?.addEventListener('resize', () => {
      if (isEditable() || document.body?.classList.contains('rtcg-mobile-keyboard-open')) scheduleVisualViewport();
    }, { passive: true });
    document.addEventListener('focusin', () => scheduleVisualViewport(true), true);
    document.addEventListener('focusout', () => window.setTimeout(() => scheduleVisualViewport(), 120), true);
  };

  const visualShellAnchorAt = (event) => {
    if (!mobileViewport.matches || document.body?.classList.contains('rtcg-shell-external-owner') || document.body?.classList.contains('rtcg-mobile-keyboard-open')) return null;
    const shell = document.querySelector('[data-rtcg-mobile-shell]');
    if (!shell || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return null;
    const style = window.getComputedStyle(shell);
    if (style.visibility === 'hidden' || style.display === 'none' || style.pointerEvents === 'none') return null;
    const anchors = [...shell.querySelectorAll(':scope > a')];
    if (!anchors.length) return null;
    const vv = window.visualViewport;
    const viewportHeight = vv?.height || window.innerHeight;
    const viewportWidth = vv?.width || window.innerWidth;
    const shellHeight = shell.offsetHeight || 64;
    const expectedTop = viewportHeight - shellHeight;
    // A small tolerance covers sub-pixel viewport rounding and the safe-area
    // seam without turning normal page content into navigation.
    if (event.clientY < expectedTop - 8 || event.clientY > viewportHeight + 8 || event.clientX < 0 || event.clientX > viewportWidth) return null;
    const index = Math.min(anchors.length - 1, Math.max(0, Math.floor(event.clientX / (viewportWidth / anchors.length))));
    return anchors[index] || null;
  };

  const installShellHitRetargeting = () => {
    if (window.__rtcgMobileShellRetargeting) return;
    window.__rtcgMobileShellRetargeting = true;
    document.addEventListener('click', event => {
      if (retargetingShellTap || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
      if (path.some(node => node?.matches?.('[data-rtcg-mobile-shell] > a'))) return;
      const anchor = visualShellAnchorAt(event);
      if (!anchor) return;
      // WebKit may visually paint the fixed bar where its native hit-test tree
      // still sees the page beneath it. Capture that physical tap at document
      // level, suppress the accidental underlying activation, and replay the
      // intended nav anchor exactly once. This also makes safe-area padding a
      // real hit surface rather than dead chrome.
      event.preventDefault();
      event.stopImmediatePropagation();
      retargetingShellTap = true;
      try { anchor.click(); } finally { retargetingShellTap = false; }
    }, true);
  };
  class RareTCGMobileShop {
    constructor(dialog) {
      this.dialog = dialog;
      this.layers = new Map();
      this.panels = [...dialog.querySelectorAll('[data-rtcg-shop-panel]')];
      this.title = dialog.querySelector('[data-rtcg-shop-title]');
      this.backButton = dialog.querySelector('[data-rtcg-shop-back]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.frames = [];
      this.path = [];
      this.pending = false;
      this.cleanup = [];
      this.inerted = new Map();
      this.shell = document.querySelector('[data-rtcg-mobile-shell]');
      this.backdrop = document.querySelector('[data-rtcg-mobile-backdrop]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.unsubscribeCart = window.RareTCGCartState?.subscribe(state => this.updateCart(state));
      if (this.backdrop) this.listen(this.backdrop, 'click', () => this.close());
      if (this.shell) this.listen(this.shell, 'keydown', event => { if (this.activeDialog.open) this.wrapTab(this.activeDialog, event); });
      this.listen(dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      this.listen(dialog, 'keydown', event => this.wrapTab(dialog, event));
      this.listen(dialog, 'animationend', event => { if (event.target === dialog && event.animationName === 'rtcg-shop-enter') dialog.style.willChange = ''; });
      this.listen(dialog, 'click', event => {
        if (this.isBackdrop(dialog, event)) this.close();
        const summary = event.target.closest('summary');
        if (summary?.parentElement.matches('[data-rtcg-inline]') && !summary.parentElement.open) {
          dialog.querySelectorAll('[data-rtcg-inline][open]').forEach(group => {
            if (group !== summary.parentElement) group.open = false;
          });
          requestAnimationFrame(() => {
            if (summary.isConnected && dialog.open) summary.scrollIntoView({ block: 'nearest' });
          });
        }
        const language = event.target.closest('[data-rtcg-language]');
        if (language) {
          const group = language.closest('[data-rtcg-inline]');
          const selected = language.getAttribute('aria-controls');
          group.querySelectorAll('[data-rtcg-language]').forEach(button => {
            button.setAttribute('aria-pressed', String(button === language));
          });
          group.querySelectorAll('[data-rtcg-language-panel]').forEach(panel => {
            panel.hidden = panel.id !== selected;
          });
          const currentLabel = group.querySelector('[data-rtcg-language-current]');
          if (currentLabel) currentLabel.textContent = language.textContent.trim();
        }
        const next = event.target.closest('[data-rtcg-shop-next]');
        if (next) this.next(next.getAttribute('aria-controls'), next);
        if (event.target.closest('[data-rtcg-shop-back]')) this.back();
        if (event.target.closest('[data-rtcg-shop-close]')) this.close();
        const link = event.target.closest('a[href]');
        if (link && !event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && !event.button && !link.target && !link.hasAttribute('download')) {
          event.preventDefault();
          this.navigate(link.href);
        }
      });
      this.listen(window, 'popstate', event => this.onPopState(event));
      this.listen(this.mobile, 'change', () => this.onBreakpoint());
      this.listen(window, 'pagehide', () => this.reset());
      this.listen(window, 'pageshow', event => { if (event.persisted) this.reset(); });
    }
    isBackdrop(dialog, event) {
      if (event.target !== dialog) return false;
      const box = dialog.getBoundingClientRect();
      return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
    }
    wrapTab(dialog, event) {
        if (event.key === 'Escape' && dialog.open) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab' || !dialog.open) return;
        // Native modal inertness is retained, but browsers can Tab from the
        // final control into chrome/body. Wrap only at the visible boundaries.
        const candidates = [
          ...dialog.querySelectorAll('a[href], button, summary, input, select, textarea, [tabindex]'),
          ...(this.shell?.querySelectorAll('a[href], button, [tabindex]') || [])
        ];
        const tabbables = [...new Set(candidates)].filter(el =>
          el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[hidden], [inert]') &&
          el.getClientRects().length > 0 && window.getComputedStyle(el).visibility === 'visible');
        const first = tabbables[0], last = tabbables.at(-1);
        const active = document.activeElement;
        if (!first || (event.shiftKey ? active === first || !tabbables.includes(active) : active === last || !dialog.contains(active))) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus({ preventScroll: true });
          if (!first) this.title.focus({ preventScroll: true });
        }
    }
    get activeDialog() { return this.activeLayer?.dialog || this.dialog; }
    registerLayer(name, dialog, adapter) {
      if (this.layers.has(name)) return;
      this.layers.set(name, { name, dialog, adapter });
      this.listen(dialog, 'keydown', event => this.wrapTab(dialog, event));
      this.listen(dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      this.listen(dialog, 'close', () => { if (this.activeLayer?.dialog === dialog) this.close(); });
      this.listen(dialog, 'animationend', event => {
        if (event.target === dialog && (event.animationName === 'rtcg-shop-enter' || event.animationName === 'rtcg-cart-enter')) dialog.style.willChange = '';
      });
      this.listen(dialog, 'click', event => {
        if (event.target.closest('[data-rtcg-layer-close]') || this.isBackdrop(dialog, event)) this.close();
        const link = event.target.closest('a[href]');
        if (link && !event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && !event.button && !link.target && !link.hasAttribute('download')) {
          event.preventDefault(); this.navigate(link.href);
        }
      });
    }
    openLayer(name, opener) {
      const layer = this.layers.get(name);
      if (!layer || !this.mobile.matches || this.activeDialog.open || this.pending || this.hasExternalOwner()) return false;
      this.activeLayer = layer;
      this.opener = opener;
      this.token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.path = [name];
      this.push();
      this.show();
      layer.adapter.open();
      return true;
    }
    // Single-tap ownership handoff: closing the current owner and opening the
    // requested destination as one atomic transition, so a nav tap while Shop
    // or another layer is open never requires a second tap to actually land.
    switchTo(name, opener) {
      if (this.pending || this.hasExternalOwner()) return false;
      const isShop = name === 'shop';
      if (!isShop && !this.layers.has(name)) {
        // Unknown destination: still resolve any currently-open owner so the
        // shell never gets stuck mid-transition, but do not open anything.
        if (this.activeDialog.open) this.hide(false);
        return false;
      }
      const currentlyOpen = this.activeDialog.open;
      const currentlyIsTarget = currentlyOpen && (isShop ? !this.activeLayer : this.activeLayer?.name === name);
      if (!currentlyOpen) return isShop ? (this.open(opener), true) : this.openLayer(name, opener);
      if (currentlyIsTarget) return false;
      // Replace the owned history entry in place rather than pushing a new
      // one, so Back from the new destination returns to the original page.
      // Keep the body lock/backdrop alive during the handoff. The previous
      // implementation called hide() + show(), which briefly unlocked the
      // document, restored scroll, then locked it again on every bottom-nav
      // switch. On iOS that produced a visible hitch even though the state
      // machine was correct.
      const previousLayer = this.activeLayer;
      const previousDialog = this.activeDialog;
      this.activeLayer = null;
      previousDialog.close();
      previousLayer?.adapter.close();
      previousDialog.style.willChange = '';
      this.panels.forEach(panel => { panel.hidden = true; panel.inert = true; });
      this.clearIsolation();

      this.opener = opener;
      this.token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.path = [isShop ? this.panels[0].id : name];
      if (window.history.state?.[KEY]) {
        window.history.replaceState({ ...window.history.state, [KEY]: { token: this.token, path: [...this.path] } }, '', window.location.href);
      } else {
        this.push();
      }

      if (isShop) {
        this.frames = [{ id: this.panels[0].id, scroll: 0 }];
        this.isolate(this.dialog);
        this.dialog.show();
        this.dialog.style.willChange = this.reducedMotion.matches ? '' : 'transform, opacity';
        this.render();
      } else {
        this.activeLayer = this.layers.get(name);
        this.isolate(this.activeDialog);
        this.activeDialog.show();
        this.activeDialog.style.willChange = this.reducedMotion.matches ? '' : 'transform, opacity';
        this.activeLayer.adapter.open();
      }
      document.body.classList.add('rtcg-shop-open');
      this.setOpeners(true);
      return true;
    }
    listen(target, type, callback) {
      target.addEventListener(type, callback);
      this.cleanup.push(() => target.removeEventListener?.(type, callback));
    }
    destroy() {
      this.reset();
      this.layers.forEach(layer => layer.adapter.destroy?.());
      this.layers.clear();
      this.unsubscribeCart?.();
      this.observer?.disconnect();
      this.cleanup.splice(0).forEach(remove => remove());
      this.clearIsolation();
      this.dialog.rtcgShop = null;
    }
    updateCart(state) {
      if (!state) return;
      const count = Math.max(0, Number(state.item_count) || 0);
      document.querySelectorAll('[data-rtcg-mobile-cart-count]').forEach(badge => {
        badge.hidden = count === 0;
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.setAttribute('aria-label', `${count} items in cart`);
      });
    }
    syncOwnership(external = this.hasExternalOwner()) {
      const bar = document.querySelector('[data-rtcg-mobile-shell]');
      const active = this.mobile.matches && external;
      if (this.externalOwner === active) return;
      this.externalOwner = active;
      if (active && this.activeDialog.open) this.close(false);
      document.body.classList.toggle('rtcg-shell-external-owner', active);
      if (bar) bar.inert = active;
    }
    hasExternalOwner() {
      // Search remains header-owned; cart, facets and app dialogs keep their
      // existing traps. Never put a second native modal above consent.
      if (document.querySelector('shopify-chat[open], body.rtcg-search-open, .rtcg-facet-drawer.is-open, cart-drawer.active, cart-notification .cart-notification.active, quick-add-modal[open], product-modal[open]')) return true;
      const visible = el => el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden';
      if ([...document.querySelectorAll('#smile-ui-container .smile-panel-frame-container')].some(visible)) return true;
      if ([...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], .MuiDialog-root')].some(el => el !== this.dialog && !this.dialog.contains(el) && ![...this.layers.values()].some(layer => el === layer.dialog || layer.dialog.contains(el)) && visible(el))) return true;
      return [...document.querySelectorAll('pandectes-cmp')].some(host => host.shadowRoot?.querySelector('dialog[open]'));
    }
    open(opener) {
      if (!this.mobile.matches || this.activeDialog.open || this.pending) return;
      this.opener = opener;
      this.token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.frames = [{ id: this.panels[0].id, scroll: 0 }];
      this.path = [this.panels[0].id];
      // Preserve the page entry's scroll before body locking resets it.
      this.push();
      this.show();
      this.render();
    }
    clearIsolation() {
      this.inerted.forEach((wasInert, el) => { if (el?.isConnected) el.inert = wasInert; });
      this.inerted.clear();
      if (this.backdrop) this.backdrop.hidden = true;
    }
    isolate(dialog) {
      this.clearIsolation();
      const exempt = new Set([this.shell, this.backdrop]);
      let node = dialog;
      while (node && node !== document.body) {
        const parent = node.parentElement;
        if (!parent) break;
        [...parent.children].forEach(sibling => {
          if (sibling === node || exempt.has(sibling) || sibling.matches('script, style, link, template')) return;
          if (!this.inerted.has(sibling)) this.inerted.set(sibling, Boolean(sibling.inert));
          sibling.inert = true;
        });
        node = parent;
      }
      if (this.backdrop) this.backdrop.hidden = false;
    }
    show() {
      this.scroll = { x: window.scrollX, y: window.scrollY };
      const style = document.body.style;
      this.bodyStyle = { position: style.position, top: style.top, width: style.width, overflow: style.overflow };
      Object.assign(style, { position: 'fixed', top: `-${this.scroll.y}px`, width: '100%', overflow: 'hidden' });
      this.isolate(this.activeDialog);
      this.activeDialog.show();
      this.activeDialog.style.willChange = this.reducedMotion.matches ? '' : 'transform, opacity';
      document.body.classList.add('rtcg-shop-open');
      this.setOpeners(true);
    }
    setOpeners(open) {
      const active = this.activeLayer?.name || 'shop';
      ['shop', 'search', 'cart'].forEach(name => {
        document.querySelectorAll(`[data-rtcg-${name}-open]`).forEach(el => el.setAttribute('aria-expanded', String(open && name === active)));
      });
    }
    push() {
      window.history.pushState({ ...window.history.state, [KEY]: { token: this.token, path: [...this.path] } }, '', window.location.href);
    }
    next(id, trigger) {
      if (!this.dialog.open || this.pending || !this.panels.some(panel => panel.id === id)) return;
      const current = this.panels.find(panel => panel.id === this.path.at(-1));
      const frame = this.frames[this.path.length - 1];
      frame.scroll = current.scrollTop;
      frame.trigger = trigger;
      this.frames = this.frames.slice(0, this.path.length);
      this.frames.push({ id, scroll: 0 });
      this.path.push(id);
      this.push();
      this.render();
    }
    render(returning = false) {
      const id = this.path.at(-1);
      this.panels.forEach(panel => {
        panel.hidden = panel.id !== id;
        panel.inert = panel.hidden;
        if (!panel.hidden) {
          this.title.textContent = panel.dataset.title;
          panel.scrollTop = this.frames[this.path.length - 1]?.scroll || 0;
        }
      });
      this.dialog.querySelectorAll('[data-rtcg-shop-next]').forEach(button => {
        button.setAttribute('aria-expanded', String(this.path.includes(button.getAttribute('aria-controls'))));
      });
      this.backButton.hidden = this.path.length < 2;
      const focus = returning && this.frames[this.path.length - 1]?.trigger;
      (focus || this.title).focus({ preventScroll: true });
    }
    back() {
      if (this.pending || !this.dialog.open) return;
      window.history.back();
    }
    close(returnFocus = true) {
      if ((!this.activeDialog.open && !this.activeLayer) || this.pending) return;
      const owned = window.history.state?.[KEY];
      const token = this.token;
      this.hide(returnFocus);
      if (owned?.token === token) {
        this.pending = true;
        window.history.go(-owned.path.length);
      }
    }
    hide(returnFocus = true) {
      if (!this.activeDialog.open && !this.activeLayer) return;
      const layer = this.activeLayer;
      this.activeLayer = null;
      (layer?.dialog || this.dialog).close();
      layer?.adapter.close();
      if (layer) this.token = null;
      const closedDialog = layer?.dialog || this.dialog;
      closedDialog.style.willChange = '';
      this.panels.forEach(panel => { panel.hidden = true; panel.inert = true; });
      document.body.classList.remove('rtcg-shop-open');
      this.clearIsolation();
      this.setOpeners(false);
      Object.assign(document.body.style, this.bodyStyle);
      window.scrollTo(this.scroll.x, this.scroll.y);
      if (returnFocus && !layer?.adapter.restoreFocus?.(this.opener)) {
        const available = el => el?.isConnected && !el.matches(':disabled, [aria-disabled="true"]') &&
          !el.closest('[hidden], [inert]') && el.getClientRects().length > 0 &&
          window.getComputedStyle(el).visibility === 'visible';
        const target = available(this.opener) ? this.opener :
          [...document.querySelectorAll('.header [data-rtcg-shop-open], .header a[href]')].find(available);
        target?.focus({ preventScroll: true });
      }
    }
    navigate(url) {
      this.destination = url;
      this.close();
      if (!this.pending) {
        this.destination = null;
        window.location.assign(url);
      }
    }
    onBreakpoint() {
      if (!this.mobile.matches) this.close();
      this.syncOwnership();
    }
    reset() {
      this.hide();
      this.pending = false;
      this.destination = null;
      this.token = null;
      if (window.history.state?.[KEY]) {
        const state = { ...window.history.state };
        delete state[KEY];
        window.history.replaceState(state, '', window.location.href);
      }
    }
    onPopState(event) {
      const owned = event.state?.[KEY];
      this.pending = false;
      if (this.destination) {
        const url = this.destination;
        this.destination = null;
        window.location.assign(url);
        return;
      }
      if (!this.token || !owned || owned.token !== this.token || !Array.isArray(owned.path) ||
          !owned.path.length || !owned.path.every(id => this.panels.some(panel => panel.id === id)) || !this.mobile.matches) {
        this.hide();
        return;
      }
      if (this.hasExternalOwner()) {
        this.hide(false);
        this.syncOwnership(true);
        return;
      }
      const returning = owned.path.length < this.path.length;
      this.path = [...owned.path];
      if (!this.dialog.open) this.show();
      this.render(returning);
    }
  }
  window.RareTCGMobileShop = RareTCGMobileShop;
  let delegated = false;
  window.initRareTCGMobileShell = () => {
    installVisualViewportTracking();
    installShellHitRetargeting();
    const dialog = document.querySelector('[data-rtcg-shop]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (!dialog.rtcgShop) {
      const shop = new RareTCGMobileShop(dialog);
      dialog.rtcgShop = shop;
      document.querySelectorAll('[data-rtcg-shop-open]').forEach(opener => opener.setAttribute('aria-controls', dialog.id));
      shop.syncOwnership();
      if (typeof MutationObserver !== 'undefined') {
        let scheduled = false, disposed = false, roots = [];
        const options = { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'open', 'aria-hidden', 'style'] };
        const refreshRoots = () => {
          window.RareTCGPerfAudit?.record?.('cmpShadowScans');
          const next = [...document.querySelectorAll('pandectes-cmp')].map(host => host.shadowRoot).filter(Boolean);
          if (next.length === roots.length && next.every(root => roots.includes(root))) return false;
          roots = next;
          observer.disconnect();
          observer.observe(document.body, options);
          roots.forEach(root => observer.observe(root, options));
          return true;
        };
        const observer = new MutationObserver(() => {
          window.RareTCGPerfAudit?.record?.('mobileMutationCallbacks');
          // MOB-603: the shell can only take ownership on phone/tablet viewports.
          // Do not run expensive consent/dialog reconciliation for desktop mutations.
          if (scheduled || disposed || !shop.mobile.matches || document.visibilityState === 'hidden') return;
          scheduled = true;
          requestAnimationFrame(() => {
            scheduled = false;
            if (disposed || !shop.mobile.matches || document.visibilityState === 'hidden') return;
            refreshRoots();
            shop.syncOwnership();
          });
        });
        observer.observe(document.body, options);
        refreshRoots();
        // attachShadow itself emits no light-DOM mutation. Discover late roots
        // without patching the app; their subsequent mutations are observed.
        const discovery = setInterval(() => {
          window.RareTCGPerfAudit?.record?.('cmpPollTicks');
          // MOB-603: shadow-root discovery remains a fallback for late CMPs.
          // Visible mobile pages: 30 checks/min, previously 120 checks/min.
          // Every nav tap still verifies external modal ownership synchronously.
          if (disposed || !shop.mobile.matches || document.visibilityState === 'hidden') return;
          if (refreshRoots()) shop.syncOwnership();
        }, 2000);
        shop.cleanup.push(() => { disposed = true; clearInterval(discovery); roots = []; });
        shop.observer = observer;
      }
    }
    if (delegated) return;
    delegated = true;
    document.addEventListener('shopify:section:load', () => window.initRareTCGMobileShell());
    document.addEventListener('shopify:section:unload', event => {
      event.target.querySelector('[data-rtcg-shop]')?.rtcgShop?.destroy();
    });
    document.addEventListener('click', event => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
      const opener = path.find(node => node?.matches?.('[data-rtcg-shop-open]')) || event.target.closest?.('[data-rtcg-shop-open]');
      if (!opener || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
      const shop = document.querySelector('[data-rtcg-shop]')?.rtcgShop;
      if (!shop?.mobile.matches) return;
      // Re-check ownership synchronously at tap time rather than trusting the
      // debounced/interval-reconciled cache, so a tap landing just after an
      // external panel (Rewards/Help/consent) closes isn't silently dropped.
      const nowExternal = shop.hasExternalOwner();
      if (shop.externalOwner !== nowExternal) shop.syncOwnership(nowExternal);
      if (nowExternal) return;
      event.preventDefault();
      shop.activeDialog.open ? shop.switchTo('shop', opener) : shop.open(opener);
    });
  };
  window.initRareTCGMobileShell();
})();
